import formatMessage from 'format-message'
import isEqual from 'lodash.isequal'
import { reader as endpoints } from '../api_urls'
import { convertStringBool } from '../utils'
import {
  addCaliperData,
  initializeReaderAnalyticsData,
  removeCaliperData,
  toggleAnalyticsUpdated,
  updatePreviousAnalyticsData,
  updateReadingAnalyticsData,
  updateVideoAnalyticsData,
} from './ReaderAnalyticsActions'

import Player from '@vimeo/player'

formatMessage.setup({
  // don't console.warn or throw an error when a translation is missing
  missingTranslation: 'ignore',
})

let initialState = {
  STATUS_TEXT: [
    formatMessage('Updated'),
    formatMessage('Update Progress'),
    formatMessage('Updating...'),
    formatMessage('Update Error'),
  ],
  // Periodic sync is a checkpoint, not the primary path: payloads are cumulative
  // and the server merges on page_load, so a skipped interval is recovered by the
  // next sync or the unload beacon. Kept long to reduce load on record_analytics.
  ANALYTICS_SYNC_RATE: 300000,
  READING_TIME_IN_SECONDS: 0.5,
  READING_PERIODIC_CHECK_SECONDS: 5,
  VIDEO_BLOCK_IN_SECONDS: 5,
  statusButtonID: '#booktools_progress_button',
  analyticsData: {},
  previousAnalyticsData: {},
  chapterID: null,
  pageID: null,
  pageLoadDate: null,
  analyticsUpdated: false,
}

let reducer = (state = initialState, action) => {
  switch (action.type) {
    case 'INITIALIZE_ANALYTICS_STORE':
      return Object.assign({}, state, {
        chapterID: action.chapterID,
        pageID: action.pageID,
        pageLoadDate: action.pageLoadDate,
        analyticsData: action.analyticsData,
      })
    case 'TOGGLE_ANALYTICS_UPDATED':
      return Object.assign({}, state, {
        analyticsUpdated: action.payload,
      })
    case 'UPDATE_PREVIOUS_ANALYTICS_DATA':
      return Object.assign({}, state, {
        previousAnalyticsData: $.extend(true, {}, action.payload),
      })
    case 'UPDATE_READING_ANALYTICS_DATA':
      return Object.assign({}, state, {
        analyticsData: {
          ...state.analyticsData,
          paras: {
            ...state.analyticsData.paras,
            [action.paragraphID]: action.value,
          },
        },
      })
    case 'UPDATE_VIDEO_ANALYTICS_DATA':
      return Object.assign({}, state, {
        analyticsData: {
          ...state.analyticsData,
          videos: {
            ...state.analyticsData.videos,
            [action.fileref]: action.statsList,
          },
        },
      })
    case 'ADD_CALIPER_DATA':
      return Object.assign({}, state, {
        analyticsData: {
          ...state.analyticsData,
          caliper: {
            ...state.analyticsData.caliper,
            [action.fileref]: action.caliperAnalytics,
          },
        },
      })
    case 'REMOVE_CALIPER_DATA':
      return Object.assign({}, state, {
        analyticsData: {
          ...state.analyticsData,
          caliper: {},
        },
      })
    default:
      return state
  }
}

const createSimpleStore = reducer => {
  let state = reducer(undefined, { type: '@@INIT' })
  let listeners = []
  return {
    getState: () => state,
    dispatch: action => {
      state = reducer(state, action)
      listeners.forEach(l => l())
    },
    subscribe: listener => {
      listeners.push(listener)
      return () => {
        listeners = listeners.filter(l => l !== listener)
      }
    },
  }
}

let analyticsStore = createSimpleStore(reducer)
window.MyEdAnalyticsStore = analyticsStore
let store = window.MyEdAnalyticsStore

/**
  Returns `true` if there are no analytics to send
*/
let isAnalyticsEmpty = () => {
  let state = store.getState()
  if ($.isEmptyObject(state.previousAnalyticsData)) {
    return false
  }

  if (isEqual(state.analyticsData, state.previousAnalyticsData)) {
    return true
  }

  return false
}

/**
  Class that handles recording and submitting reader analytics to the server
*/
export class ReaderAnalytics {
  constructor(isUserAnonymous, chapterID, pageID, pageLoadDate) {
    this.isUserAnonymous = convertStringBool(isUserAnonymous)
    this.saveAnalyticsToServerTriggered = false
    this._syncFailureCount = 0
    this._retryTimeout = null
    this._vimeoPlayerCache = new Map()
    let analyticsData = {
      paras: {},
      total_paras: $('#bookcontent').find('p.para').length,
      videos: {},
      caliper: {},
    }
    store.dispatch(
      initializeReaderAnalyticsData(
        chapterID,
        pageID,
        pageLoadDate,
        analyticsData
      )
    )
  }

  /**
      Setup and start the reader analytics handlers
    */
  start() {
    try {
      this._setUpPageUnloadHandlers()
    } catch (e) {
      console.error('ReaderAnalytics: failed to set up page unload handlers', e)
    }

    try {
      this._setUpReadingAnalyticsHandler()
    } catch (e) {
      console.error(
        'ReaderAnalytics: failed to set up reading analytics handler',
        e
      )
    }

    try {
      this._attachAnalyticsSyncTimer()
    } catch (e) {
      console.error('ReaderAnalytics: failed to attach analytics sync timer', e)
    }

    try {
      this._attachSyncAnalyticsFuncToGlobal()
    } catch (e) {
      console.error(
        'ReaderAnalytics: failed to attach sync function to global',
        e
      )
    }

    try {
      // vimeo analytics handlers are called immediately for any
      // videos already on screen and in a loop for items
      // that need to be scrolled to
      this._setUpVimeoVideoPlayerAnalyticsHandler()
      setInterval(() => {
        try {
          this._setUpVimeoVideoPlayerAnalyticsHandler()
        } catch (e) {
          console.error('ReaderAnalytics: error in Vimeo handler interval', e)
        }
      }, 2000)
    } catch (e) {
      console.error(
        'ReaderAnalytics: failed to set up Vimeo analytics handler',
        e
      )
    }

    try {
      //due to how youtube loads its analytics API it calls its own
      //setInterval internally
      this._setUpYouTubeVideoPlayerAnalyticsHandler()
    } catch (e) {
      console.error(
        'ReaderAnalytics: failed to set up YouTube analytics handler',
        e
      )
    }
  }

  /**
      Sync analytics to server via AJAX (used for periodic syncs while the page is active)

      @param syncOverride optional parameter that ensures method performs ajax call
    */
  syncAnalytics(syncOverride = false) {
    try {
      let state = store.getState()
      if (
        ($(state.statusButtonID).text() === state.STATUS_TEXT[2] ||
          isAnalyticsEmpty()) &&
        !syncOverride
      ) {
        store.dispatch(toggleAnalyticsUpdated(false))
        return
      }

      if (window.updateStatusButton) {
        window.updateStatusButton(2, true)
      }

      let chapterID = state.chapterID
      let pageID = state.pageID
      let url = endpoints.recordAnalytics({ chapterID, pageID })

      $.ajax({
        url: url,
        type: 'POST',
        dataType: 'html',
        progressBarStyle: false,
        data: {
          page_load: state.pageLoadDate,
          last_page: location.pathname,
          analytics_data: JSON.stringify(state.analyticsData),
        },
        success: (data, _, response) => {
          this._syncFailureCount = 0
          if (response.getResponseHeader('me-response-type') === 'error') {
            if (window.updateStatusButton) {
              window.updateStatusButton(3, true)
            }
            $.meDialog.topMessage(data)
            store.dispatch(toggleAnalyticsUpdated(false))
          } else {
            if (window.updateStatusButton) {
              window.updateStatusButton(0, true)
            }
            store.dispatch(toggleAnalyticsUpdated(true))
            store.dispatch(updatePreviousAnalyticsData(state.analyticsData))
          }
          store.dispatch(removeCaliperData())
        },
        error: () => {
          console.error('ReaderAnalytics: error syncing analytics')
          if (window.updateStatusButton) {
            window.updateStatusButton(3, true)
          }
          store.dispatch(toggleAnalyticsUpdated(false))
          this._scheduleRetry()
        },
      })
    } catch (e) {
      console.error('ReaderAnalytics: exception in syncAnalytics', e)
    }
  }

  /**
      Schedule a retry with exponential backoff after a failed sync.
      Retries at 30s, 60s, 120s, 240s, 300s then gives up.
    */
  _scheduleRetry() {
    this._syncFailureCount++
    if (this._syncFailureCount > 5) {
      return
    }
    let delay = Math.min(
      30000 * Math.pow(2, this._syncFailureCount - 1),
      300000
    )
    if (this._retryTimeout) {
      clearTimeout(this._retryTimeout)
    }
    this._retryTimeout = setTimeout(() => {
      this._retryTimeout = null
      this.syncAnalytics(true)
    }, delay)
  }

  /**
      Send analytics via navigator.sendBeacon (for page unload / visibility change).
      sendBeacon is guaranteed to be queued by the browser even during page unload,
      unlike async XHR which gets killed on Safari iOS and other mobile browsers.
      Returns true if beacon was sent, false otherwise.
    */
  _sendBeacon() {
    try {
      if (this.isUserAnonymous || isAnalyticsEmpty()) {
        return false
      }

      let state = store.getState()
      let chapterID = state.chapterID
      let pageID = state.pageID
      let url = endpoints.recordAnalytics({ chapterID, pageID })

      let formData = new FormData()
      formData.append('page_load', state.pageLoadDate)
      formData.append('last_page', location.pathname)
      formData.append('analytics_data', JSON.stringify(state.analyticsData))

      // sendBeacon can't set custom headers, so include CSRF token as a form field
      let csrfMatch = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/)
      if (csrfMatch) {
        formData.append('csrfmiddlewaretoken', csrfMatch[1])
      }

      if (navigator.sendBeacon) {
        let sent = navigator.sendBeacon(url, formData)
        if (sent) {
          store.dispatch(updatePreviousAnalyticsData(state.analyticsData))
          store.dispatch(removeCaliperData())
        }
        return sent
      }

      // Fallback for very old browsers without sendBeacon support
      $.ajax({
        url: url,
        type: 'POST',
        data: {
          page_load: state.pageLoadDate,
          last_page: location.pathname,
          analytics_data: JSON.stringify(state.analyticsData),
        },
        async: false,
      })
      return true
    } catch (e) {
      console.error('ReaderAnalytics: error sending beacon', e)
      return false
    }
  }

  /**
        Attach `syncAnalytics` func to global object

      This is done so `booktools.advanced_options.jsm` can call the `syncAnalytics()`
      method in the proper context.
    */
  _attachSyncAnalyticsFuncToGlobal() {
    window.MyEd_syncAnalytics = this.syncAnalytics.bind(this)
  }

  /**
      Attach analytics sync timer to DOM
    */
  _attachAnalyticsSyncTimer() {
    let state = store.getState()
    if (!this.isUserAnonymous) {
      let timerEl = $('#reader_content')
      if (timerEl.length === 0) {
        console.error(
          'ReaderAnalytics: #reader_content not found, sync timer will not run'
        )
      }
      timerEl.powerTimer({
        sleep: state.ANALYTICS_SYNC_RATE,
        name: 'analytics_sync_timer',
        func: () => {
          this.syncAnalytics()
        },
      })
    }
  }

  /**
      Sync analytics to server on page unload / visibility change.
      Uses sendBeacon for reliability across all browsers (especially Safari iOS).
    */
  _setUpPageUnloadHandlers() {
    // visibilitychange is the most reliable event across platforms, including
    // Safari iOS where beforeunload is not guaranteed to fire.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.saveAnalyticsToServerTriggered = true
        this._sendBeacon()
      } else if (document.visibilityState === 'visible') {
        // Reset flag so analytics resume when the user returns to the tab
        this.saveAnalyticsToServerTriggered = false
      }
    })

    // pagehide fires when the page is actually being unloaded (navigation, tab close).
    // On Safari iOS this is more reliable than beforeunload.
    window.addEventListener('pagehide', () => {
      if (!this.saveAnalyticsToServerTriggered) {
        this.saveAnalyticsToServerTriggered = true
        this._sendBeacon()
      }
    })

    // beforeunload is kept for desktop browsers where it is well-supported.
    $(window)
      .off('beforeunload.save_analytics')
      .on('beforeunload.save_analytics', () => {
        if (!this.saveAnalyticsToServerTriggered) {
          this.saveAnalyticsToServerTriggered = true
          this._sendBeacon()
        }
      })
  }

  /**
      Returns an array of visible paragraph elements within the #reader_content
      scroll container. Uses getBoundingClientRect for accurate visibility detection
      regardless of which element is scrolling.
    */
  _getVisibleParagraphs() {
    let container = document.getElementById('reader_content')
    if (!container) {
      return []
    }
    let containerRect = container.getBoundingClientRect()
    let visible = []
    let paras = document.querySelectorAll('#bookcontent p.para')
    for (let i = 0; i < paras.length; i++) {
      let para = paras[i]
      if (para.offsetParent === null) {
        continue // hidden via display:none
      }
      let rect = para.getBoundingClientRect()
      // Check if the paragraph overlaps with the container's visible area
      if (rect.bottom > containerRect.top && rect.top < containerRect.bottom) {
        visible.push(para)
      }
    }
    return visible
  }

  /**
      Record reading timestamps for all currently visible paragraphs
    */
  _recordVisibleParagraphs() {
    let now = new Date().getTime()
    let visibleParas = this._getVisibleParagraphs()
    for (let i = 0; i < visibleParas.length; i++) {
      let id = visibleParas[i].getAttribute('id')
      if (id) {
        store.dispatch(updateReadingAnalyticsData(id, now))
      }
    }
    if (visibleParas.length > 0 && window.updateStatusButton) {
      window.updateStatusButton()
    }
  }

  /**
      Attach reading analytics handler to window
    */
  _setUpReadingAnalyticsHandler() {
    let state = store.getState()

    // Track reading on scroll/resize (debounced)
    $('#reader_content').on(
      'scroll.reading_log_watcher resize.reading_log_watcher',
      () => {
        $('#bookcontent').powerTimer({
          name: 'reading_log_watcher',
          delay: state.READING_TIME_IN_SECONDS * 1000.0,
          func: () => {
            this._recordVisibleParagraphs()
          },
        })
      }
    )

    // Fire once on load so initial viewport is captured
    $('#reader_content').trigger('scroll.reading_log_watcher')

    // Periodic check so reading time is tracked even without scrolling
    // (e.g. user reads a page that fits on screen without scrolling)
    if (!this.isUserAnonymous) {
      $('#bookcontent').powerTimer({
        name: 'reading_periodic_check',
        sleep: state.READING_PERIODIC_CHECK_SECONDS * 1000.0,
        func: () => {
          this._recordVisibleParagraphs()
        },
      })
    }
  }

  /**
     Attach Vimeo Video Player analytics handler to DOM
   */
  _setUpVimeoVideoPlayerAnalyticsHandler() {
    var helperFunctionContext = this
    $("iframe[data-video-player='vimeo']").each(function () {
      let guid = $(this).attr('data-vimeo-player-guid')
      let fileref = $(this).attr('data-vimeo-id')
      let already_setup = $(this).attr('data-vimeo-handler-setup')
      if (!guid || !fileref || already_setup) {
        return
      }
      $(this).attr('data-vimeo-handler-setup', true)

      try {
        let vimeoPlayer = new Player($(this).get(0))
        // Cache the player instance for reuse in pausePlayers
        helperFunctionContext._vimeoPlayerCache.set(guid, vimeoPlayer)

        vimeoPlayer.ready().then(() => {
          let playing = false
          let lastData = null
          let volume = 1
          let muted = true
          let count = 0 // this is hacky, but mute/unmute events trigger 2x on the first click, waiting for vimeo to fix the issue, will just ignore the second trigger for now
          vimeoPlayer.getVolume().then(vol => {
            volume = vol
            if (volume > 0) {
              muted = false
            }
          })

          let lastPlayerUpdate = 0

          vimeoPlayer.on('timeupdate', data => {
            try {
              if (!playing) {
                helperFunctionContext.recordVideoPlayerTime(fileref, true, 0, 0)
                pausePlayers()
              }
              playing = true
              lastData = data
              if ($.now() - lastPlayerUpdate > 1000) {
                helperFunctionContext.recordVideoPlayerTime(
                  fileref,
                  false,
                  data.seconds,
                  data.duration
                )
                lastPlayerUpdate = $.now()
              }
            } catch (e) {
              console.error('ReaderAnalytics: error in Vimeo timeupdate', e)
            }
          })

          vimeoPlayer.on('play', data => {
            try {
              helperFunctionContext.recordCaliperAnalytics(
                fileref,
                'Started',
                data.seconds,
                data.duration,
                Date.now()
              )
            } catch (e) {
              console.error('ReaderAnalytics: error in Vimeo play', e)
            }
          })

          vimeoPlayer.on('pause', () => {
            try {
              playing = false
              if (lastData) {
                helperFunctionContext.recordVideoPlayerTime(
                  fileref,
                  false,
                  lastData.seconds,
                  lastData.duration
                )
                helperFunctionContext.recordCaliperAnalytics(
                  fileref,
                  'Paused',
                  lastData.seconds,
                  lastData.duration,
                  Date.now()
                )
              }
            } catch (e) {
              console.error('ReaderAnalytics: error in Vimeo pause', e)
            }
          })

          vimeoPlayer.on('ended', () => {
            try {
              playing = false
              if (lastData) {
                helperFunctionContext.recordVideoPlayerTime(
                  fileref,
                  false,
                  lastData.seconds,
                  lastData.duration
                )
                helperFunctionContext.recordCaliperAnalytics(
                  fileref,
                  'Ended',
                  lastData.seconds,
                  lastData.duration,
                  Date.now()
                )
              }
            } catch (e) {
              console.error('ReaderAnalytics: error in Vimeo ended', e)
            }
          })

          vimeoPlayer.on('volumechange', data => {
            try {
              count++
              if (count != 2) {
                // this will be removed when vimeo fixes their api
                if (muted && data.volume > 0) {
                  muted = !muted
                  helperFunctionContext.recordCaliperAnalytics(
                    fileref,
                    'Unmuted',
                    lastData.seconds,
                    lastData.duration,
                    Date.now(),
                    [{ volume: data.volume }]
                  )
                } else if (!muted && (data.volume == 0 || data.volume == 1)) {
                  muted = !muted
                  helperFunctionContext.recordCaliperAnalytics(
                    fileref,
                    'Muted',
                    lastData.seconds,
                    lastData.duration,
                    Date.now()
                  )
                }
              }
            } catch (e) {
              console.error('ReaderAnalytics: error in Vimeo volumechange', e)
            }
          })

          vimeoPlayer.on('playbackratechange', data => {
            try {
              helperFunctionContext.recordCaliperAnalytics(
                fileref,
                'ChangedSpeed',
                lastData.seconds,
                lastData.duration,
                Date.now(),
                [{ playbackRate: data.playbackRate }]
              )
            } catch (e) {
              console.error(
                'ReaderAnalytics: error in Vimeo playbackratechange',
                e
              )
            }
          })

          vimeoPlayer.on('seeked', data => {
            try {
              helperFunctionContext.recordCaliperAnalytics(
                fileref,
                'JumpedTo',
                data.seconds,
                data.duration,
                Date.now()
              )
            } catch (e) {
              console.error('ReaderAnalytics: error in Vimeo seeked', e)
            }
          })

          let pausePlayers = () => {
            helperFunctionContext._vimeoPlayerCache.forEach(
              (cachedPlayer, cachedGuid) => {
                // Prune stale entries whose iframes are no longer in the DOM
                let iframe = document.querySelector(
                  `iframe[data-vimeo-player-guid="${cachedGuid}"]`
                )
                if (!iframe) {
                  helperFunctionContext._vimeoPlayerCache.delete(cachedGuid)
                  return
                }
                if (cachedGuid !== guid) {
                  try {
                    cachedPlayer.getPaused().then(paused => {
                      if (!paused) {
                        cachedPlayer.pause()
                      }
                    })
                  } catch (e) {
                    // Player may have been destroyed
                    helperFunctionContext._vimeoPlayerCache.delete(cachedGuid)
                  }
                }
              }
            )
          }
        })
      } catch (e) {
        console.error('ReaderAnalytics: error setting up Vimeo player', e)
      }
    })
  }

  /**
      Attach YouTube Video Player analytics handler to DOM
    */
  _setUpYouTubeVideoPlayerAnalyticsHandler() {
    // Youtube API requires that this function is available at global scope
    if ($('#youtube_api_script_tag').length === 0) {
      let tag = document.createElement('script')
      tag.src = 'https://www.youtube.com/iframe_api'
      tag.id = 'youtube_api_script_tag'

      // Detect load failures (ad blockers, network errors)
      tag.onerror = () => {
        console.warn(
          'ReaderAnalytics: YouTube iframe API failed to load (may be blocked by ad blocker or network issue)'
        )
      }

      let firstScriptTag = document.getElementsByTagName('script')[0]
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag)

      // Timeout to detect silent load failures
      setTimeout(() => {
        if (
          typeof window.YT === 'undefined' ||
          typeof window.YT.Player === 'undefined'
        ) {
          console.warn(
            'ReaderAnalytics: YouTube iframe API did not load within 15 seconds'
          )
        }
      }, 15000)
    }

    window.onYouTubeIframeAPIReady = () => {
      setInterval(() => {
        var helperFunctionContext = this
        $("iframe[data-video-player='youtube']").each(function () {
          let div = $(this)
          let divID = div.attr('id')
          let guid = div.attr('data-youtube-player-guid')
          let fileref = div.attr('data-youtube-id')
          let alreadySetup = div.attr('data-analytics-handler-ready')
          if (!guid || !fileref || alreadySetup) {
            return
          }
          div.attr('data-analytics-handler-ready', true)

          try {
            let onPlayerStateChange = () => {
              try {
                if (player.getPlayerState() === YT.PlayerState.PLAYING) {
                  onPlayerPlay({})

                  $(this).powerTimer({
                    name: 'youtube_player_event_timer',
                    sleepAfter: 500,
                    func: () => {
                      if (player.getPlayerState() === YT.PlayerState.PLAYING) {
                        onPlayerProgress({
                          seconds: player.getCurrentTime(),
                          duration: player.getDuration(),
                        })
                      } else {
                        return false
                      }
                    },
                  })
                } else if (player.getPlayerState() === YT.PlayerState.PAUSED) {
                  $(this).powerTimer('stop')
                  onPlayerPause({
                    seconds: player.getCurrentTime(),
                    duration: player.getDuration(),
                  })
                } else if (player.getPlayerState() === YT.PlayerState.ENDED) {
                  $(this).powerTimer('stop')
                  onPlayerFinish({
                    seconds: player.getCurrentTime(),
                    duration: player.getDuration(),
                  })
                } else {
                  $(this).powerTimer('stop')
                }
              } catch (e) {
                console.error(
                  'ReaderAnalytics: error in YouTube state change',
                  e
                )
              }
            }

            let onPlayerPlay = data => {
              pausePlayers()
              helperFunctionContext.recordVideoPlayerTime(fileref, true, 0, 0)
              helperFunctionContext.recordCaliperAnalytics(
                fileref,
                'Started',
                data.seconds,
                data.duration,
                Date.now()
              )
            }

            let onPlayerProgress = data => {
              helperFunctionContext.recordVideoPlayerTime(
                fileref,
                false,
                data.seconds,
                data.duration
              )
            }

            let onPlayerPause = data => {
              helperFunctionContext.recordVideoPlayerTime(
                fileref,
                false,
                data.seconds,
                data.duration
              )
              helperFunctionContext.recordCaliperAnalytics(
                fileref,
                'Paused',
                data.seconds,
                data.duration,
                Date.now()
              )
            }

            let onPlayerFinish = data => {
              helperFunctionContext.recordVideoPlayerTime(
                fileref,
                false,
                data.seconds,
                data.duration
              )
              helperFunctionContext.recordCaliperAnalytics(
                fileref,
                'Ended',
                data.seconds,
                data.duration,
                Date.now()
              )
            }

            let pausePlayers = () => {
              $('iframe[data-youtube-player-guid]').each(
                (index, videoPlayer) => {
                  if (
                    $(videoPlayer).attr('data-youtube-player-guid') !== guid
                  ) {
                    let otherPlayer = $(videoPlayer).data('youtube_player')
                    if (
                      otherPlayer &&
                      otherPlayer.getPlayerState() === YT.PlayerState.PLAYING
                    ) {
                      otherPlayer.pauseVideo()
                    }
                  }
                }
              )
            }
            let player = new YT.Player(divID, {
              width: div.attr('data-width') || 400,
              height: div.attr('data-height') || 250,
              videoId: div.attr('data-youtube-id'),
              playerVars: {
                autoplay: 0,
                controls: 1,
                allowFullScreen: 1,
              },
              events: {
                onStateChange: onPlayerStateChange,
              },
            })
            $(this).data('youtube_player', player)
          } catch (e) {
            console.error('ReaderAnalytics: error setting up YouTube player', e)
          }
        })
      }, 1000)
    }
  }
  /**
      Helper method that adds caliper media analytics to be sent to the backend
    */
  recordCaliperAnalytics(
    fileref,
    action,
    positionTime,
    duration,
    time,
    args = []
  ) {
    let state = store.getState()
    let caliperStatsList = state.analyticsData.caliper[fileref] || []

    caliperStatsList.push({
      actionTaken: action,
      positionTime: positionTime,
      videoDuration: duration,
      eventTime: time,
      args: args,
    })

    store.dispatch(addCaliperData(fileref, caliperStatsList))
    return caliperStatsList[caliperStatsList.length - 1]
  }

  /**
      Helper method that updates video player statistics for the current player
    */
  recordVideoPlayerTime(fileref, newRecord, currentSeconds, totalSeconds) {
    let state = store.getState()
    let now = new Date().getTime()
    let stats = this.getVideoPlayerStats(fileref, newRecord)

    if (stats.play === 0) {
      stats.play = now
    }

    stats.pause = now

    if (totalSeconds > 0) {
      stats.total_blocks = Math.ceil(
        totalSeconds / state.VIDEO_BLOCK_IN_SECONDS
      )
    }

    if (currentSeconds >= 0) {
      let currentBlock = Math.floor(
        currentSeconds / state.VIDEO_BLOCK_IN_SECONDS
      )
      stats.blocks[currentBlock] = now
    }

    if (window.updateStatusButton) {
      window.updateStatusButton()
    }
  }

  /**
      Helper method that retrieves video player statistics for a given fileref
    */
  getVideoPlayerStats(fileref, newRecord) {
    let state = store.getState()
    let statsList = state.analyticsData.videos[fileref] || []
    if (newRecord || statsList.length === 0) {
      statsList.push({
        play: 0,
        pause: 0,
        blocks: {},
        total_blocks: 0,
      })
    }

    store.dispatch(updateVideoAnalyticsData(fileref, statsList))
    return statsList[statsList.length - 1]
  }
}
